// Small pure edits the filter panel makes to a `MapFilter` (MapFilterSheet.toggle / DaysPreset). Kept apart from the
// component so the case-insensitive id toggling and the day-range presets are unit-tested.

import type { MapFilter } from '@/domain/records';
import { sameId } from '@/domain/util';

/** Adds or removes an id (any case; the stored ids keep their original case). */
export function toggleId(ids: readonly string[], id: string): string[] {
  return ids.some((x) => sameId(x, id)) ? ids.filter((x) => !sameId(x, id)) : [...ids, id];
}

/** Adds or removes a plain value (types, priorities, tags). */
export function toggleValue<T>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

export type DayBoundKey = 'daysSinceVisitMin' | 'daysSinceVisitMax' | 'daysSinceActivityMin' | 'daysSinceActivityMax';

/** Sets or clears (undefined) one day bound; the key is removed rather than stored as undefined. */
export function setDayBound(f: MapFilter, key: DayBoundKey, value: number | undefined): MapFilter {
  const next: MapFilter = { ...f };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

export interface DayRangePreset {
  id: 'any' | 'week' | 'month' | 'quarter' | 'older';
  title: string;
  min: number | undefined;
  max: number | undefined;
}

/** `MapFilterSheet.DaysPreset`: Any, 0–7, 8–30, 31–90, 90+ (91 and up). */
export const DAY_RANGE_PRESETS: readonly DayRangePreset[] = [
  { id: 'any', title: 'Any', min: undefined, max: undefined },
  { id: 'week', title: '0–7', min: 0, max: 7 },
  { id: 'month', title: '8–30', min: 8, max: 30 },
  { id: 'quarter', title: '31–90', min: 31, max: 90 },
  { id: 'older', title: '90+', min: 91, max: undefined },
];

/** A preset chip is on when both bounds equal its bounds (so "Any" is on when neither is set). */
export function dayRangePresetOn(f: MapFilter, minKey: DayBoundKey, maxKey: DayBoundKey, p: DayRangePreset): boolean {
  return f[minKey] === p.min && f[maxKey] === p.max;
}
